import uuidv4 from 'uuid/v4';
import { saveExperiments } from 'redux/modules/experiments/actions/save-experiments';
import { setMonetateRequestCookie } from 'redux/modules/experiments/actions/set-monetate-request-cookie';
import { getAllCookies } from 'utils/cookie';
import { userAgent as getUserAgent } from 'utils/user-agent';
import { fetchMonetateActions } from 'redux/modules/experiments/actions/fetch-monetate-actions';
import { buildRequestBody } from 'redux/modules/experiments/actions/build-request-body';
import { getRequestPropertiesFromCookies } from 'redux/modules/experiments/actions/get-request-properties-from-cookies';
import { getIsSsrPage } from 'redux/modules/routing/selectors/index';
import {
  getIsMonetateBypassed,
  getMonetatePreview,
  getMonetateIpAddress,
} from 'redux/modules/experiments/selectors';
import { MonetateActions } from 'redux/modules/experiments/actions/types';
import {
  getCustomerSlotBranchId,
  getCustomerSlotDate,
  getSlotType,
} from 'redux/modules/customer-slot/selectors';
import { getCustomerOrderTotal } from 'redux/modules/accounts/selectors/get-customer-account';
import { marketingPreferencesAcknowledged as getMarketingPreferencesAcknowledged } from 'redux/modules/my-account/selectors';
import { getCustomerId, isUserLoggedIn } from 'redux/modules/sessions/selectors';
import { Fetcher } from 'route-data/fetcher-types';

/**
 * This is required for any page that uses Monetate for A/B testing.
 */
export const clientSideMonetateFetcher: Fetcher =
  ({ match: { pathname: path } }): WtrThunkAction<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const isSsrPage = getIsSsrPage(state);
    // @ts-expect-error getIsMonetateBypassed is not typed
    const isMonetateBypassed = getIsMonetateBypassed(state);

    if (__SERVER__ || isSsrPage || isMonetateBypassed) {
      return;
    }

    const branchId = getCustomerSlotBranchId(state);
    const customerId = getCustomerId(state);
    const marketingPreferencesAcknowledged = getMarketingPreferencesAcknowledged(state);
    const slotDate = getCustomerSlotDate(state);
    const slotType = getSlotType(state);
    const totalOrderCount = getCustomerOrderTotal(state);
    const isLoggedIn = isUserLoggedIn(state);

    // Server-side, when the `user-agent` is `undefined` we set `bypassMonetate`
    // to `true` in state. Hence the `user-agent` here won't be `undefined` as we
    // exit early with `isMonetateBypassed` above.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const userAgent = getUserAgent().ua!;

    const cookies = getAllCookies();
    const { monetateId } = getRequestPropertiesFromCookies(cookies);
    const requestId = uuidv4();

    // @ts-expect-error getMonetatePreview is not typed
    const preview = getMonetatePreview(state);

    // @ts-expect-error getMonetateIpAddress is not typed
    const ipAddress = getMonetateIpAddress(state);

    const requestBody = buildRequestBody({
      branchId,
      customerId,
      ipAddress,
      marketingPreferencesAcknowledged,
      monetateId,
      path,
      preview,
      requestId,
      slotDate,
      slotType,
      totalOrderCount,
      url: path,
      userAgent,
      isLoggedIn,
    });

    const monetateActions = await fetchMonetateActions({
      requestBody,
      requestId,
      timeout: {
        response: 2000,
        deadline: 2000,
      },
    });

    if (!monetateActions) {
      return;
    }

    const {
      decisions,
      payloads,
      impressions,
      monetateId: responseMonetateId,
    } = monetateActions as MonetateActions;

    if (responseMonetateId) {
      dispatch(setMonetateRequestCookie(responseMonetateId));
    }

    dispatch(saveExperiments({ decisions, payloads, impressions }));
  };
